import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { claimsApi, documentsApi, checklistApi, preauthApi, denialsApi, arFollowupApi, paymentsApi, claimEmailsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  PencilSimple,
  PaperPlaneTilt,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Hospital,
  Calendar,
  FileText,
  Upload,
  Download,
  Trash,
  Plus,
  Warning,
  Phone,
  Envelope,
  CurrencyInr,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { ClaimEmailsTab, ComposeEmailDialog } from './components/ClaimEmailsSection';
import { EditDocumentDialog, EditPreauthDialog, EditDenialDialog, EditFollowupDialog, EditPaymentDialog } from './components/ClaimEditDialogs';

const CLAIM_STATUSES = [
  'registered', 'eligibility_pending', 'eligibility_verified', 
  'preauth_pending', 'preauth_approved', 'preauth_rejected',
  'documents_pending', 'documents_complete', 'coding_pending', 'coding_complete',
  'bill_preparation', 'claim_ready', 'submitted', 'query_raised',
  'denied', 'under_appeal', 'payment_pending', 'partially_settled', 'settled', 'closed', 'written_off'
];

const DOCUMENT_TYPES = [
  { value: 'discharge_summary', label: 'Discharge Summary' },
  { value: 'final_bill', label: 'Final Bill' },
  { value: 'investigation_reports', label: 'Investigation Reports' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'policy_copy', label: 'Policy Copy' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'pre_auth_letter', label: 'Pre-Auth Letter' },
  { value: 'claim_form', label: 'Claim Form' },
  { value: 'medical_certificate', label: 'Medical Certificate' },
  { value: 'surgery_notes', label: 'Surgery Notes' },
  { value: 'other', label: 'Other' },
];

const DENIAL_CATEGORIES = [
  { value: 'documentation_incomplete', label: 'Documentation Incomplete' },
  { value: 'policy_not_active', label: 'Policy Not Active' },
  { value: 'pre_existing_condition', label: 'Pre-existing Condition' },
  { value: 'non_covered_service', label: 'Non-covered Service' },
  { value: 'exceeds_policy_limit', label: 'Exceeds Policy Limit' },
  { value: 'late_submission', label: 'Late Submission' },
  { value: 'incorrect_coding', label: 'Incorrect Coding' },
  { value: 'authorization_not_obtained', label: 'Authorization Not Obtained' },
  { value: 'other', label: 'Other' },
];

const formatStatus = (status) => {
  return status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || '';
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value || 0);
};

const ClaimDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Pre-auth state
  const [preauths, setPreauths] = useState([]);
  const [preauthDialogOpen, setPreauthDialogOpen] = useState(false);
  const [preauthForm, setPreauthForm] = useState({
    requested_amount: '',
    diagnosis: '',
    procedure: '',
    expected_los: '',
    notes: ''
  });
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [partialPreauthId, setPartialPreauthId] = useState(null);
  const [partialForm, setPartialForm] = useState({ approved_amount: '', approved_los: '', notes: '' });
  
  // Denial state
  const [denials, setDenials] = useState([]);
  const [denialDialogOpen, setDenialDialogOpen] = useState(false);
  const [denialForm, setDenialForm] = useState({
    category: '',
    reason: '',
    deduction_amount: '',
    responsible_department: '',
    notes: ''
  });
  
  // AR Follow-up state
  const [followups, setFollowups] = useState([]);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [followupForm, setFollowupForm] = useState({
    followup_type: 'call',
    contact_person: '',
    notes: '',
    next_followup_date: '',
    expected_payment_date: ''
  });
  
  // Edit state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [allowedStatuses, setAllowedStatuses] = useState([]);
  const [canPostPayment, setCanPostPayment] = useState(false);

  // Edit states for sub-modules
  const [editDocDialog, setEditDocDialog] = useState(null);
  const [editDocForm, setEditDocForm] = useState({ document_type: '', notes: '' });
  const [editPreauthDialog, setEditPreauthDialog] = useState(null);
  const [editPreauthForm, setEditPreauthForm] = useState({ requested_amount: '', expected_los: '', diagnosis: '', notes: '' });
  const [editDenialDialog, setEditDenialDialog] = useState(null);
  const [editDenialForm, setEditDenialForm] = useState({ category: '', reason: '', deduction_amount: '', notes: '' });
  const [editFollowupDialog, setEditFollowupDialog] = useState(null);
  const [editFollowupForm, setEditFollowupForm] = useState({ followup_type: '', contact_person: '', notes: '', next_action: '' });
  const [editPaymentDialog, setEditPaymentDialog] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({ amount: '', payment_mode: '', reference_number: '', payment_date: '', notes: '' });
  
  // Payments state
  const [claimPayments, setClaimPayments] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  // Email state
  const [emails, setEmails] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '', attachment_ids: [] });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'neft',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    deduction_amount: '0',
    deduction_reason: '',
    notes: ''
  });

  useEffect(() => {
    loadAllData();
  }, [id]);

  const loadAllData = async () => {
    try {
      const [claimRes, docsRes, checklistRes, preauthRes, denialsRes, followupsRes, paymentsRes] = await Promise.all([
        claimsApi.get(id),
        documentsApi.list(id),
        checklistApi.list(id),
        preauthApi.list({ claim_id: id }),
        denialsApi.list({ claim_id: id }),
        arFollowupApi.getForClaim(id),
        paymentsApi.getForClaim(id)
      ]);
      
      setClaim(claimRes.data);
      setDocuments(docsRes.data);
      setChecklist(checklistRes.data);
      setPreauths(preauthRes.data);
      setDenials(denialsRes.data);
      setFollowups(followupsRes.data);
      setClaimPayments(paymentsRes.data);

      // Load emails
      try {
        const emailRes = await claimEmailsApi.history(id);
        setEmails(emailRes.data || []);
      } catch {}
      
      setEditData({
        status: claimRes.data.status,
        billed_amount: claimRes.data.billed_amount || 0,
        approved_amount: claimRes.data.approved_amount || 0,
        paid_amount: claimRes.data.paid_amount || 0,
        deduction_amount: claimRes.data.deduction_amount || 0,
        denial_reason: claimRes.data.denial_reason || '',
        notes: claimRes.data.notes || '',
        insurance_claim_no: claimRes.data.insurance_claim_no || '',
      });
      
      // Load allowed status transitions
      try {
        const statusRes = await claimsApi.allowedStatuses(id);
        setAllowedStatuses(statusRes.data.allowed_statuses || []);
        setCanPostPayment(statusRes.data.can_post_payment || false);
      } catch { setAllowedStatuses(CLAIM_STATUSES); setCanPostPayment(true); }
      
      // Create checklist if empty
      if (checklistRes.data.length === 0) {
        await checklistApi.create(id);
        const newChecklist = await checklistApi.list(id);
        setChecklist(newChecklist.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load claim details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await claimsApi.update(id, editData);
      toast.success('Claim updated successfully');
      setEditing(false);
      loadAllData();
    } catch (error) {
      toast.error('Failed to update claim');
    }
  };

  const handleSubmit = async () => {
    try {
      await claimsApi.submit(id);
      toast.success('Claim submitted successfully');
      loadAllData();
    } catch (error) {
      toast.error('Failed to submit claim');
    }
  };

  // Email handlers
  const openComposeEmail = async (templateId) => {
    setEmailLoading(true);
    try {
      const [compRes, tplRes] = await Promise.all([
        claimEmailsApi.compose(id, templateId || ''),
        claimEmailsApi.templates(),
      ]);
      setEmailForm({
        to: compRes.data.to || '',
        cc: compRes.data.cc || '',
        subject: compRes.data.subject || '',
        body: compRes.data.body || '',
        attachment_ids: (compRes.data.attachments || []).filter(a => a.selected).map(a => a.id),
      });
      setEmailAttachments(compRes.data.attachments || []);
      setEmailTemplates(tplRes.data || []);
      setEmailDialogOpen(true);
    } catch (e) { toast.error('Failed to load email compose'); }
    finally { setEmailLoading(false); }
  };

  const handleTemplateChange = async (tplId) => {
    setSelectedTemplate(tplId);
    if (tplId) {
      try {
        const res = await claimEmailsApi.compose(id, tplId);
        setEmailForm(f => ({ ...f, subject: res.data.subject, body: res.data.body }));
      } catch {}
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.body) {
      toast.error('To, Subject, and Body are required'); return;
    }
    setSendingEmail(true);
    try {
      const res = await claimEmailsApi.send({
        claim_id: id, to: emailForm.to, cc: emailForm.cc,
        subject: emailForm.subject, body: emailForm.body,
        attachment_ids: emailForm.attachment_ids,
      });
      if (res.data.status === 'sent') {
        toast.success('Email sent successfully');
        setEmailDialogOpen(false);
        const emailRes = await claimEmailsApi.history(id);
        setEmails(emailRes.data || []);
      } else {
        toast.error(`Email failed: ${res.data.error}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSendingEmail(false); }
  };

  const toggleAttachment = (attId) => {
    setEmailForm(f => ({
      ...f,
      attachment_ids: f.attachment_ids.includes(attId)
        ? f.attachment_ids.filter(a => a !== attId)
        : [...f.attachment_ids, attId],
    }));
  };


  // Document handlers
  const handleUpload = async () => {
    if (!uploadFile || !uploadType) {
      toast.error('Please select a file and document type');
      return;
    }
    
    setUploading(true);
    try {
      await documentsApi.upload(id, uploadType, uploadFile, uploadNotes);
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadType('');
      setUploadNotes('');
      loadAllData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsApi.download(doc.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsApi.delete(docId);
        toast.success('Document deleted');
        loadAllData();
      } catch (error) {
        toast.error('Failed to delete document');
      }
    }
  };

  // Pre-auth handlers
  const handleCreatePreauth = async () => {
    try {
      await preauthApi.create({
        claim_id: id,
        ...preauthForm,
        requested_amount: parseFloat(preauthForm.requested_amount) || 0,
        expected_los: parseInt(preauthForm.expected_los) || 1
      });
      toast.success('Pre-authorization created');
      setPreauthDialogOpen(false);
      setPreauthForm({ requested_amount: '', diagnosis: '', procedure: '', expected_los: '', notes: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to create pre-authorization');
    }
  };

  const handleUpdatePreauth = async (preauthId, status, data = {}) => {
    try {
      await preauthApi.update(preauthId, { status, ...data });
      toast.success(`Pre-auth ${status.replace('_', ' ')}`);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pre-authorization');
    }
  };

  const openPartialDialog = (pa) => {
    setPartialPreauthId(pa.id);
    setPartialForm({ approved_amount: '', approved_los: pa.expected_los || '', notes: '' });
    setPartialDialogOpen(true);
  };

  const handlePartialApproval = async () => {
    const amt = parseFloat(partialForm.approved_amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid approved amount'); return; }
    try {
      await preauthApi.update(partialPreauthId, {
        status: 'partial_approved',
        approved_amount: amt,
        approved_los: parseInt(partialForm.approved_los) || 0,
        notes: partialForm.notes || '',
      });
      toast.success('Pre-auth partially approved');
      setPartialDialogOpen(false);
      setPartialPreauthId(null);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  // Denial handlers
  const handleCreateDenial = async () => {
    try {
      await denialsApi.create({
        claim_id: id,
        ...denialForm,
        deduction_amount: parseFloat(denialForm.deduction_amount) || 0
      });
      toast.success('Denial recorded');
      setDenialDialogOpen(false);
      setDenialForm({ category: '', reason: '', deduction_amount: '', responsible_department: '', notes: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to record denial');
    }
  };

  const handleUpdateDenial = async (denialId, status) => {
    try {
      await denialsApi.update(denialId, { status });
      toast.success(`Denial ${status}`);
      loadAllData();
    } catch (error) {
      toast.error('Failed to update denial');
    }
  };

  // AR Follow-up handlers
  const handleCreateFollowup = async () => {
    try {
      await arFollowupApi.create({
        claim_id: id,
        ...followupForm
      });
      toast.success('Follow-up recorded');
      setFollowupDialogOpen(false);
      setFollowupForm({ followup_type: 'call', contact_person: '', notes: '', next_followup_date: '', expected_payment_date: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to record follow-up');
    }
  };

  // Helper for error toast
  const showError = (e, fallback = 'Failed') => {
    const d = e.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(', ') : fallback);
  };

  // ===== Edit handlers for sub-modules =====
  const handleEditDoc = (doc) => {
    setEditDocForm({ document_type: doc.document_type || '', notes: doc.notes || '' });
    setEditDocDialog(doc);
  };
  const handleSaveDoc = async () => {
    try {
      await documentsApi.update(editDocDialog.id, editDocForm);
      toast.success('Document updated');
      setEditDocDialog(null);
      loadAllData();
    } catch (e) { showError(e); }
  };

  const handleEditPreauth = (pa) => {
    setEditPreauthForm({ requested_amount: pa.requested_amount || '', approved_amount: pa.approved_amount || '', expected_los: pa.expected_los || '', approved_los: pa.approved_los || '', diagnosis: pa.diagnosis || '', notes: pa.notes || '' });
    setEditPreauthDialog(pa);
  };
  const handleSavePreauth = async () => {
    try {
      const d = {};
      if (editPreauthForm.requested_amount) d.requested_amount = parseFloat(editPreauthForm.requested_amount) || 0;
      if (editPreauthForm.approved_amount) d.approved_amount = parseFloat(editPreauthForm.approved_amount) || 0;
      if (editPreauthForm.expected_los) d.expected_los = parseInt(editPreauthForm.expected_los) || 0;
      if (editPreauthForm.approved_los) d.approved_los = parseInt(editPreauthForm.approved_los) || 0;
      if (editPreauthForm.diagnosis) d.diagnosis = editPreauthForm.diagnosis;
      if (editPreauthForm.notes) d.notes = editPreauthForm.notes;
      await preauthApi.update(editPreauthDialog.id, d);
      toast.success('Pre-auth updated');
      setEditPreauthDialog(null);
      loadAllData();
    } catch (e) { showError(e); }
  };

  const handleEditDenial = (d) => {
    setEditDenialForm({ category: d.category || '', reason: d.reason || '', deduction_amount: d.deduction_amount || '', notes: d.notes || '' });
    setEditDenialDialog(d);
  };
  const handleSaveDenial = async () => {
    try {
      const d = {};
      if (editDenialForm.category) d.category = editDenialForm.category;
      if (editDenialForm.reason) d.reason = editDenialForm.reason;
      d.deduction_amount = parseFloat(editDenialForm.deduction_amount) || 0;
      if (editDenialForm.notes) d.notes = editDenialForm.notes;
      await denialsApi.update(editDenialDialog.id, d);
      toast.success('Denial updated');
      setEditDenialDialog(null);
      loadAllData();
    } catch (e) { showError(e); }
  };

  const handleEditFollowup = (f) => {
    setEditFollowupForm({ followup_type: f.followup_type || 'call', contact_person: f.contact_person || '', notes: f.notes || '', next_action: f.next_action || '' });
    setEditFollowupDialog(f);
  };
  const handleSaveFollowup = async () => {
    try {
      await arFollowupApi.update(editFollowupDialog.id, editFollowupForm);
      toast.success('Follow-up updated');
      setEditFollowupDialog(null);
      loadAllData();
    } catch (e) { showError(e); }
  };
  const handleDeleteFollowup = async (fId) => {
    if (!window.confirm('Delete this follow-up?')) return;
    try { await arFollowupApi.delete(fId); toast.success('Deleted'); loadAllData(); }
    catch (e) { showError(e); }
  };

  const handleEditPayment = (p) => {
    setEditPaymentForm({ amount: p.amount || '', payment_mode: p.payment_mode || '', reference_number: p.reference_number || '', payment_date: p.payment_date || '', notes: p.notes || '' });
    setEditPaymentDialog(p);
  };
  const handleSavePayment = async () => {
    try {
      const d = {};
      if (editPaymentForm.amount) d.amount = parseFloat(editPaymentForm.amount) || 0;
      if (editPaymentForm.payment_mode) d.payment_mode = editPaymentForm.payment_mode;
      if (editPaymentForm.reference_number) d.reference_number = editPaymentForm.reference_number;
      if (editPaymentForm.payment_date) d.payment_date = editPaymentForm.payment_date;
      if (editPaymentForm.notes) d.notes = editPaymentForm.notes;
      await paymentsApi.update(editPaymentDialog.id, d);
      toast.success('Payment updated');
      setEditPaymentDialog(null);
      loadAllData();
    } catch (e) { showError(e); }
  };
  const handleDeletePayment = async (pId) => {
    if (!window.confirm('Delete this payment? The amount will be reversed on the claim.')) return;
    try { await paymentsApi.delete(pId); toast.success('Payment deleted & reversed'); loadAllData(); }
    catch (e) { showError(e); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D4A3E]"></div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="text-center py-12">
        <p className="text-[#595959]">Claim not found</p>
        <Button onClick={() => navigate('/claims')} className="mt-4">Back to Claims</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="claim-detail-page">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/claims')} className="text-[#595959]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-light font-['Outfit'] text-[#1C1C1C]">{claim.claim_number}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`status-${claim.status}`}>{formatStatus(claim.status)}</Badge>
              <span className="text-[#595959]">•</span>
              <span className="text-[#595959]">{claim.patient_name}</span>
              {claim.insurance_claim_no && (
                <>
                  <span className="text-[#595959]">•</span>
                  <span className="text-xs font-mono text-orange-600 bg-orange-50 px-2 py-0.5 rounded">Claim No: {claim.insurance_claim_no}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50 gap-1.5"
            onClick={() => openComposeEmail()} disabled={emailLoading} data-testid="send-email-btn">
            <Envelope className="h-4 w-4" /> {emailLoading ? 'Loading...' : 'Send Email'}
          </Button>
          {claim.status === 'claim_ready' && (
            <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" onClick={handleSubmit} data-testid="submit-claim-btn">
              <PaperPlaneTilt className="h-4 w-4 mr-2" />
              Submit to Payer
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[#F5F5F0] p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-white">
            Documents {documents.length > 0 && <Badge className="ml-1 bg-[#2D4A3E] text-white">{documents.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="preauth" className="data-[state=active]:bg-white">
            Pre-Auth {preauths.length > 0 && <Badge className="ml-1 bg-blue-100 text-blue-800">{preauths.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="denials" className="data-[state=active]:bg-white">
            Denials {denials.length > 0 && <Badge className="ml-1 bg-red-100 text-red-800">{denials.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="ar" className="data-[state=active]:bg-white">
            AR Follow-up {followups.length > 0 && <Badge className="ml-1 bg-orange-100 text-orange-800">{followups.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-white">
            Payments {claimPayments.length > 0 && <Badge className="ml-1 bg-green-100 text-green-800">{claimPayments.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">History</TabsTrigger>
          <TabsTrigger value="emails" className="data-[state=active]:bg-white" data-testid="emails-tab">
            Emails {emails.length > 0 && <Badge className="ml-1 bg-indigo-100 text-indigo-800">{emails.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Claim Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-[#2D4A3E] mt-0.5" />
                      <div>
                        <p className="text-xs text-[#595959] uppercase tracking-wider">Patient</p>
                        <p className="font-medium text-[#1C1C1C]">{claim.patient_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Hospital className="h-5 w-5 text-[#2D4A3E] mt-0.5" />
                      <div>
                        <p className="text-xs text-[#595959] uppercase tracking-wider">Payer/TPA</p>
                        <p className="font-medium text-[#1C1C1C]">{claim.payer_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-[#2D4A3E] mt-0.5" />
                      <div>
                        <p className="text-xs text-[#595959] uppercase tracking-wider">Admission</p>
                        <p className="font-medium text-[#1C1C1C]">{claim.admission_date}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-[#2D4A3E] mt-0.5" />
                      <div>
                        <p className="text-xs text-[#595959] uppercase tracking-wider">Discharge</p>
                        <p className="font-medium text-[#1C1C1C]">{claim.discharge_date || 'Not discharged'}</p>
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-[#E5E5E5]" />

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-[#595959] uppercase tracking-wider">Department</p>
                      <p className="font-medium text-[#1C1C1C]">{claim.department || 'General'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#595959] uppercase tracking-wider">Ward</p>
                      <p className="font-medium text-[#1C1C1C]">{claim.ward || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#595959] uppercase tracking-wider">Room</p>
                      <p className="font-medium text-[#1C1C1C]">{claim.room_number || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-[#595959] uppercase tracking-wider">Claim No (from Insurance)</p>
                      <p className="font-medium text-orange-600 font-mono">{claim.insurance_claim_no || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#595959] uppercase tracking-wider">Claim Type</p>
                      <p className="font-medium text-[#1C1C1C] capitalize">{claim.claim_type || '-'}</p>
                    </div>
                  </div>

                  {(claim.diagnosis || claim.procedure) && (
                    <>
                      <Separator className="bg-[#E5E5E5]" />
                      <div className="space-y-3">
                        {claim.diagnosis && (
                          <div>
                            <p className="text-xs text-[#595959] uppercase tracking-wider">Diagnosis</p>
                            <p className="text-[#1C1C1C]">{claim.diagnosis}</p>
                          </div>
                        )}
                        {claim.procedure && (
                          <div>
                            <p className="text-xs text-[#595959] uppercase tracking-wider">Procedure</p>
                            <p className="text-[#1C1C1C]">{claim.procedure}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Financial Details */}
              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Financial Details</CardTitle>
                    {!editing && (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-sky-600 hover:bg-sky-50 h-7 px-2 gap-1 text-xs">
                        <PencilSimple className="h-3 w-3" /> Edit
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {editing ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Billed Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editData.billed_amount}
                          onChange={(e) => setEditData({ ...editData, billed_amount: parseFloat(e.target.value) || 0 })}
                          className="border-[#E5E5E5]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Approved Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editData.approved_amount}
                          onChange={(e) => setEditData({ ...editData, approved_amount: parseFloat(e.target.value) || 0 })}
                          className="border-[#E5E5E5]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Paid Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editData.paid_amount}
                          onChange={(e) => setEditData({ ...editData, paid_amount: parseFloat(e.target.value) || 0 })}
                          className="border-[#E5E5E5]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Deduction Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editData.deduction_amount}
                          onChange={(e) => setEditData({ ...editData, deduction_amount: parseFloat(e.target.value) || 0 })}
                          className="border-[#E5E5E5]"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-[#FAFAF9]">
                        <p className="text-xs text-[#595959] uppercase tracking-wider">Estimated</p>
                        <p className="text-xl font-semibold text-[#1C1C1C] font-['Outfit']">{formatCurrency(claim.estimated_amount)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-[#FAFAF9]">
                        <p className="text-xs text-[#595959] uppercase tracking-wider">Billed</p>
                        <p className="text-xl font-semibold text-[#1C1C1C] font-['Outfit']">{formatCurrency(claim.billed_amount)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-[#D1E7DD]">
                        <p className="text-xs text-[#0F5132] uppercase tracking-wider">Approved</p>
                        <p className="text-xl font-semibold text-[#0F5132] font-['Outfit']">{formatCurrency(claim.approved_amount)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-[#D1E7DD]">
                        <p className="text-xs text-[#0F5132] uppercase tracking-wider">Paid</p>
                        <p className="text-xl font-semibold text-[#0F5132] font-['Outfit']">{formatCurrency(claim.paid_amount)}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Status Update (when editing) */}
              {editing && (
                <Card className="bg-white border-[#E5E5E5]">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Update Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                        <SelectTrigger className="border-[#E5E5E5]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={claim.status}>{formatStatus(claim.status)} (Current)</SelectItem>
                          {allowedStatuses.filter(s => s !== claim.status).map(status => (
                            <SelectItem key={status} value={status}>{formatStatus(status)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {allowedStatuses.length === 0 && (
                        <p className="text-[10px] text-red-500">This is a terminal status — no further transitions allowed.</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        className="border-[#E5E5E5]"
                        placeholder="Add notes..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Claim No (from Insurance)</Label>
                      <Input
                        value={editData.insurance_claim_no || ''}
                        onChange={(e) => setEditData({ ...editData, insurance_claim_no: e.target.value })}
                        className="border-[#E5E5E5]"
                        placeholder="Claim number from insurance company"
                        data-testid="edit-insurance-claim-no"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setEditing(false)} className="border-[#E5E5E5]">Cancel</Button>
                      <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" onClick={handleUpdate}>Save Changes</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="outline" className="w-full justify-start border-[#E5E5E5]" onClick={() => { setActiveTab('documents'); setUploadDialogOpen(true); }}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                  <Button variant="outline" className="w-full justify-start border-[#E5E5E5]" onClick={() => { setActiveTab('preauth'); setPreauthDialogOpen(true); }}>
                    <FileText className="h-4 w-4 mr-2" />
                    Create Pre-Auth
                  </Button>
                  <Button variant="outline" className="w-full justify-start border-[#E5E5E5]" onClick={() => { setActiveTab('ar'); setFollowupDialogOpen(true); }}>
                    <Phone className="h-4 w-4 mr-2" />
                    Add Follow-up
                  </Button>
                </CardContent>
              </Card>

              {/* Document Status */}
              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Document Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {checklist.slice(0, 5).map((item) => (
                      <div key={item.id} className="flex items-center justify-between">
                        <span className="text-sm text-[#595959]">{DOCUMENT_TYPES.find(d => d.value === item.document_type)?.label || item.document_type}</span>
                        {item.status === 'uploaded' || item.status === 'verified' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <Clock className="h-4 w-4 text-yellow-600" />
                        )}
                      </div>
                    ))}
                    {checklist.length > 5 && (
                      <Button variant="link" className="p-0 h-auto text-[#2D4A3E]" onClick={() => setActiveTab('documents')}>
                        View all {checklist.length} items
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-['Outfit'] text-[#1C1C1C]">Documents & Checklist</h2>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" data-testid="upload-doc-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                  <DialogDescription>Upload a document for this claim</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Document Type *</Label>
                    <Select value={uploadType} onValueChange={setUploadType}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>File *</Label>
                    <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} ref={fileInputRef} />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} placeholder="Optional notes..." />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" onClick={handleUpload} disabled={uploading}>
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Checklist */}
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Document Checklist</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {checklist.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 rounded-md bg-[#FAFAF9]">
                      <div className="flex items-center gap-3">
                        {item.status === 'uploaded' || item.status === 'verified' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : item.status === 'rejected' ? (
                          <XCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-600" />
                        )}
                        <div>
                          <p className="font-medium text-[#1C1C1C]">{DOCUMENT_TYPES.find(d => d.value === item.document_type)?.label || item.document_type}</p>
                          <p className="text-xs text-[#595959]">{item.is_required ? 'Required' : 'Optional'}</p>
                        </div>
                      </div>
                      <Badge className={
                        item.status === 'uploaded' ? 'bg-blue-100 text-blue-800' :
                        item.status === 'verified' ? 'bg-green-100 text-green-800' :
                        item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>{formatStatus(item.status)}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Uploaded Documents */}
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Uploaded Documents</CardTitle>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <p className="text-[#595959] text-center py-8">No documents uploaded yet</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 rounded-md bg-[#FAFAF9]">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#2D4A3E]" />
                          <div>
                            <p className="font-medium text-[#1C1C1C] truncate max-w-[200px]">{doc.original_filename}</p>
                            <p className="text-xs text-[#595959]">{DOCUMENT_TYPES.find(d => d.value === doc.document_type)?.label}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEditDoc(doc)} title="Edit">
                            <PencilSimple className="h-4 w-4 text-sky-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-600">
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pre-Auth Tab */}
        <TabsContent value="preauth" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-['Outfit'] text-[#1C1C1C]">Pre-Authorizations</h2>
            <Dialog open={preauthDialogOpen} onOpenChange={setPreauthDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" data-testid="create-preauth-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Pre-Auth
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Pre-Authorization</DialogTitle>
                  <DialogDescription>Submit a pre-authorization request</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Requested Amount (₹) *</Label>
                      <Input type="number" value={preauthForm.requested_amount} onChange={(e) => setPreauthForm({...preauthForm, requested_amount: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Expected LOS (days) *</Label>
                      <Input type="number" value={preauthForm.expected_los} onChange={(e) => setPreauthForm({...preauthForm, expected_los: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Diagnosis *</Label>
                    <Textarea value={preauthForm.diagnosis} onChange={(e) => setPreauthForm({...preauthForm, diagnosis: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Procedure *</Label>
                    <Textarea value={preauthForm.procedure} onChange={(e) => setPreauthForm({...preauthForm, procedure: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={preauthForm.notes} onChange={(e) => setPreauthForm({...preauthForm, notes: e.target.value})} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setPreauthDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" onClick={handleCreatePreauth}>Create</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {preauths.length === 0 ? (
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-[#A3A3A3] mx-auto mb-4" />
                <p className="text-[#595959]">No pre-authorizations created yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {preauths.map((pa) => (
                <Card key={pa.id} className="bg-white border-[#E5E5E5]">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-medium text-[#1C1C1C]">{pa.preauth_number}</p>
                        <p className="text-sm text-[#595959]">Created: {new Date(pa.created_at).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEditPreauth(pa)} className="text-sky-600 hover:bg-sky-50 h-7 w-7 p-0" title="Edit">
                          <PencilSimple className="h-3.5 w-3.5" />
                        </Button>
                        <Badge className={
                          pa.status === 'approved' ? 'bg-green-100 text-green-800' :
                          pa.status === 'partial_approved' ? 'bg-amber-100 text-amber-800' :
                          pa.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          pa.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>{formatStatus(pa.status)}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Requested</p>
                        <p className="font-medium">{formatCurrency(pa.requested_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Approved</p>
                        <p className={`font-medium ${pa.approved_amount > 0 ? 'text-green-600' : ''}`}>{formatCurrency(pa.approved_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Expected LOS</p>
                        <p className="font-medium">{pa.expected_los} days</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Approved LOS</p>
                        <p className="font-medium">{pa.approved_los || '-'} days</p>
                      </div>
                    </div>

                    {/* Balance / Patient Responsibility for partial */}
                    {pa.status === 'partial_approved' && pa.requested_amount > pa.approved_amount && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-[10px] text-amber-700 uppercase font-semibold">Balance Amount</p>
                            <p className="text-lg font-bold text-amber-800">{formatCurrency(pa.requested_amount - pa.approved_amount)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-amber-700 uppercase font-semibold">Patient / Other Responsibility</p>
                            <p className="text-sm text-amber-700">To be settled from patient, co-pay, or other sources</p>
                          </div>
                          <div className="flex items-center justify-end">
                            <Badge className="bg-amber-200 text-amber-800 border-0">Partial Pre-Auth</Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {pa.status === 'draft' && (
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" onClick={() => handleUpdatePreauth(pa.id, 'submitted')}>
                          Submit to TPA
                        </Button>
                      </div>
                    )}
                    {pa.status === 'submitted' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="border-green-600 text-green-600" onClick={() => handleUpdatePreauth(pa.id, 'approved', { approved_amount: pa.requested_amount, approved_los: pa.expected_los })}>
                          Full Approved
                        </Button>
                        <Button size="sm" variant="outline" className="border-amber-600 text-amber-600" onClick={() => openPartialDialog(pa)}>
                          Partial Approved
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-600 text-red-600" onClick={() => handleUpdatePreauth(pa.id, 'rejected')}>
                          Rejected
                        </Button>
                      </div>
                    )}
                    {pa.status === 'partial_approved' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="border-green-600 text-green-600" onClick={() => handleUpdatePreauth(pa.id, 'approved', { approved_amount: pa.requested_amount, approved_los: pa.expected_los })}>
                          Upgrade to Full Approval
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Partial Approval Dialog */}
        <Dialog open={partialDialogOpen} onOpenChange={setPartialDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Partial Pre-Auth Approval</DialogTitle>
              <DialogDescription>Enter the amount approved by insurance. The balance will be marked as patient/other responsibility.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Approved Amount *</Label>
                <Input type="number" value={partialForm.approved_amount}
                  onChange={e => setPartialForm(f => ({...f, approved_amount: e.target.value}))}
                  placeholder="e.g., 30000" data-testid="partial-approved-amount" />
              </div>
              <div>
                <Label>Approved LOS (Days)</Label>
                <Input type="number" value={partialForm.approved_los}
                  onChange={e => setPartialForm(f => ({...f, approved_los: e.target.value}))}
                  placeholder="e.g., 3" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={partialForm.notes}
                  onChange={e => setPartialForm(f => ({...f, notes: e.target.value}))}
                  placeholder="Reason for partial approval" />
              </div>
              <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" onClick={handlePartialApproval} data-testid="confirm-partial-btn">
                Confirm Partial Approval
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Denials Tab */}
        <TabsContent value="denials" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-['Outfit'] text-[#1C1C1C]">Denial Management</h2>
            <Dialog open={denialDialogOpen} onOpenChange={setDenialDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#C05621] hover:bg-[#9A4519] text-white" data-testid="record-denial-btn">
                  <Warning className="h-4 w-4 mr-2" />
                  Record Denial
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record Denial</DialogTitle>
                  <DialogDescription>Document a claim denial for tracking and appeal</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={denialForm.category} onValueChange={(v) => setDenialForm({...denialForm, category: v})}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {DENIAL_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Reason *</Label>
                    <Textarea value={denialForm.reason} onChange={(e) => setDenialForm({...denialForm, reason: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Deduction Amount (₹)</Label>
                      <Input type="number" value={denialForm.deduction_amount} onChange={(e) => setDenialForm({...denialForm, deduction_amount: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Responsible Dept</Label>
                      <Input value={denialForm.responsible_department} onChange={(e) => setDenialForm({...denialForm, responsible_department: e.target.value})} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setDenialDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-[#C05621] hover:bg-[#9A4519] text-white" onClick={handleCreateDenial}>Record</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {denials.length === 0 ? (
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-[#595959]">No denials recorded for this claim</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {denials.map((denial) => (
                <Card key={denial.id} className="bg-white border-[#E5E5E5] border-l-4 border-l-red-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-medium text-[#1C1C1C]">{DENIAL_CATEGORIES.find(c => c.value === denial.category)?.label || denial.category}</p>
                        <p className="text-sm text-[#595959] mt-1">{denial.reason}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEditDenial(denial)} className="text-sky-600 hover:bg-sky-50 h-7 w-7 p-0" title="Edit">
                          <PencilSimple className="h-3.5 w-3.5" />
                        </Button>
                        <Badge className={
                          denial.status === 'recovered' ? 'bg-green-100 text-green-800' :
                          denial.status === 'under_appeal' ? 'bg-blue-100 text-blue-800' :
                          denial.status === 'written_off' ? 'bg-gray-100 text-gray-800' :
                          denial.status === 'rejected' ? 'bg-red-200 text-red-800' :
                          'bg-red-100 text-red-800'
                        }>{formatStatus(denial.status)}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Deduction</p>
                        <p className="font-medium text-red-600">{formatCurrency(denial.deduction_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Recovered</p>
                        <p className="font-medium text-green-600">{formatCurrency(denial.recovery_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#595959] uppercase">Appeals</p>
                        <p className="font-medium">{denial.appeal_count}</p>
                      </div>
                    </div>
                    {denial.status === 'open' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="border-blue-600 text-blue-600" onClick={() => handleUpdateDenial(denial.id, 'under_appeal')}>
                          Start Appeal
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-600" onClick={() => handleUpdateDenial(denial.id, 'written_off')}>
                          Write Off
                        </Button>
                      </div>
                    )}
                    {denial.status === 'under_appeal' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="border-green-600 text-green-600" onClick={() => handleUpdateDenial(denial.id, 'recovered')}>
                          Recovered / Won Appeal
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-600 text-red-600" onClick={() => handleUpdateDenial(denial.id, 'rejected')}>
                          Appeal Rejected
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-600" onClick={() => handleUpdateDenial(denial.id, 'written_off')}>
                          Write Off
                        </Button>
                      </div>
                    )}
                    {denial.status === 'rejected' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="border-blue-600 text-blue-600" onClick={() => handleUpdateDenial(denial.id, 'under_appeal')}>
                          Re-Appeal
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-600" onClick={() => handleUpdateDenial(denial.id, 'written_off')}>
                          Write Off
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* AR Follow-up Tab */}
        <TabsContent value="ar" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-['Outfit'] text-[#1C1C1C]">AR Follow-up</h2>
            <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" data-testid="add-followup-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Follow-up
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record Follow-up</DialogTitle>
                  <DialogDescription>Log a follow-up activity for this claim</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Type *</Label>
                    <Select value={followupForm.followup_type} onValueChange={(v) => setFollowupForm({...followupForm, followup_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call">Phone Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="letter">Letter</SelectItem>
                        <SelectItem value="portal_check">Portal Check</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person</Label>
                    <Input value={followupForm.contact_person} onChange={(e) => setFollowupForm({...followupForm, contact_person: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes *</Label>
                    <Textarea value={followupForm.notes} onChange={(e) => setFollowupForm({...followupForm, notes: e.target.value})} placeholder="What was discussed..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Next Follow-up</Label>
                      <Input type="date" value={followupForm.next_followup_date} onChange={(e) => setFollowupForm({...followupForm, next_followup_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Expected Payment</Label>
                      <Input type="date" value={followupForm.expected_payment_date} onChange={(e) => setFollowupForm({...followupForm, expected_payment_date: e.target.value})} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setFollowupDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" onClick={handleCreateFollowup}>Save</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {followups.length === 0 ? (
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="py-12 text-center">
                <Phone className="h-12 w-12 text-[#A3A3A3] mx-auto mb-4" />
                <p className="text-[#595959]">No follow-ups recorded yet</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F0]">
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Next Follow-up</TableHead>
                      <TableHead>By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {followups.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell className="text-[#595959]">{new Date(f.created_at).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell>
                          <Badge className="bg-[#F5F5F0] text-[#1C1C1C]">
                            {f.followup_type === 'call' && <Phone className="h-3 w-3 mr-1" />}
                            {f.followup_type === 'email' && <Envelope className="h-3 w-3 mr-1" />}
                            {formatStatus(f.followup_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>{f.contact_person || '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{f.notes}</TableCell>
                        <TableCell>{f.next_followup_date || '-'}</TableCell>
                        <TableCell className="text-[#595959]">{f.created_by_name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            <Button variant="ghost" size="sm" onClick={() => handleEditFollowup(f)} className="text-sky-600 hover:bg-sky-50 h-7 w-7 p-0" title="Edit">
                              <PencilSimple className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteFollowup(f.id)} className="text-red-500 hover:bg-red-50 h-7 w-7 p-0" title="Delete">
                              <Trash className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-[#1C1C1C]">Payments</h3>
            <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white gap-2" size="sm"
                  disabled={!canPostPayment} data-testid="add-claim-payment-btn"
                  title={canPostPayment ? 'Post Payment' : `Payment not allowed in '${formatStatus(claim?.status)}' status`}>
                  <Plus className="h-4 w-4" /> Post Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Post Payment for {claim?.claim_number}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Amount *</Label>
                      <Input type="number" placeholder="0" value={paymentForm.amount}
                        onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                        data-testid="claim-payment-amount" />
                    </div>
                    <div>
                      <Label className="text-xs">Payment Date *</Label>
                      <Input type="date" value={paymentForm.payment_date}
                        onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                        data-testid="claim-payment-date" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Mode</Label>
                      <Select value={paymentForm.payment_mode} onValueChange={v => setPaymentForm(f => ({ ...f, payment_mode: v }))}>
                        <SelectTrigger data-testid="claim-payment-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neft">NEFT</SelectItem>
                          <SelectItem value="rtgs">RTGS</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Reference #</Label>
                      <Input placeholder="UTR/Cheque" value={paymentForm.reference_number}
                        onChange={e => setPaymentForm(f => ({ ...f, reference_number: e.target.value }))}
                        data-testid="claim-payment-ref" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Deduction</Label>
                      <Input type="number" placeholder="0" value={paymentForm.deduction_amount}
                        onChange={e => setPaymentForm(f => ({ ...f, deduction_amount: e.target.value }))}
                        data-testid="claim-payment-deduction" />
                    </div>
                    <div>
                      <Label className="text-xs">Bank</Label>
                      <Input placeholder="Bank name" value={paymentForm.bank_name}
                        onChange={e => setPaymentForm(f => ({ ...f, bank_name: e.target.value }))}
                        data-testid="claim-payment-bank" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Input placeholder="Notes..." value={paymentForm.notes}
                      onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                      data-testid="claim-payment-notes" />
                  </div>
                  <Button className="w-full bg-[#2D4A3E] hover:bg-[#1F362C] text-white" data-testid="submit-claim-payment-btn"
                    onClick={async () => {
                      if (!paymentForm.amount || !paymentForm.payment_date) {
                        toast.error('Amount and date are required');
                        return;
                      }
                      try {
                        await paymentsApi.create({
                          claim_id: id,
                          amount: parseFloat(paymentForm.amount),
                          payment_mode: paymentForm.payment_mode,
                          reference_number: paymentForm.reference_number,
                          payment_date: paymentForm.payment_date,
                          bank_name: paymentForm.bank_name,
                          deduction_amount: parseFloat(paymentForm.deduction_amount || '0'),
                          deduction_reason: paymentForm.deduction_reason,
                          notes: paymentForm.notes
                        });
                        toast.success('Payment posted');
                        setPaymentDialogOpen(false);
                        setPaymentForm({ amount: '', payment_mode: 'neft', reference_number: '', payment_date: new Date().toISOString().split('T')[0], bank_name: '', deduction_amount: '0', deduction_reason: '', notes: '' });
                        loadAllData();
                      } catch (err) {
                        showError(err, 'Failed to post payment');
                      }
                    }}>
                    Post Payment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {claimPayments.length === 0 ? (
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="py-12 text-center text-[#595959]" data-testid="no-claim-payments">
                <CurrencyInr className="h-10 w-10 mx-auto mb-2 text-[#A3A3A3]" />
                <p className="text-sm">No payments recorded for this claim</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-[#E5E5E5]" data-testid="claim-payments-table">
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F0]">
                      <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Amount</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Deduction</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Mode</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Reference</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Posted By</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {claimPayments.map((p, idx) => (
                      <TableRow key={p.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF9]'} data-testid={`claim-payment-row-${idx}`}>
                        <TableCell className="text-sm">{p.payment_date}</TableCell>
                        <TableCell className="text-sm font-medium text-[#0F5132]">{formatCurrency(p.amount)}</TableCell>
                        <TableCell className="text-sm text-[#C05621]">{p.deduction_amount > 0 ? formatCurrency(p.deduction_amount) : '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs uppercase">{p.payment_mode}</Badge></TableCell>
                        <TableCell className="text-sm text-[#595959]">{p.reference_number || '-'}</TableCell>
                        <TableCell className="text-sm text-[#595959]">{p.posted_by_name || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            <Button variant="ghost" size="sm" onClick={() => handleEditPayment(p)} className="text-sky-600 hover:bg-sky-50 h-7 w-7 p-0" title="Edit">
                              <PencilSimple className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeletePayment(p.id)} className="text-red-500 hover:bg-red-50 h-7 w-7 p-0" title="Delete">
                              <Trash className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-3 pt-3 border-t border-[#E5E5E5] flex justify-between text-sm">
                  <span className="text-[#595959]">Total Payments: {claimPayments.length}</span>
                  <span className="font-medium text-[#0F5132]">
                    Total: {formatCurrency(claimPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Status History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {claim.status_history?.slice().reverse().map((entry, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`h-3 w-3 rounded-full ${index === 0 ? 'bg-[#2D4A3E]' : 'bg-[#E5E5E5]'}`}></div>
                      {index < claim.status_history.length - 1 && (
                        <div className="w-0.5 h-full bg-[#E5E5E5] my-1"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <Badge className={`status-${entry.status} mb-1`}>{formatStatus(entry.status)}</Badge>
                      <p className="text-xs text-[#595959]">{new Date(entry.timestamp).toLocaleString('en-IN')}</p>
                      <p className="text-xs text-[#595959]">by {entry.user}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== EMAILS TAB ========== */}
        <TabsContent value="emails" className="space-y-6" data-testid="emails-tab-content">
          <ClaimEmailsTab
            emails={emails}
            emailLoading={emailLoading}
            openComposeEmail={openComposeEmail}
          />
        </TabsContent>
      </Tabs>

      {/* ========== COMPOSE EMAIL DIALOG ========== */}
      <ComposeEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        emailTemplates={emailTemplates}
        selectedTemplate={selectedTemplate}
        handleTemplateChange={handleTemplateChange}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        emailAttachments={emailAttachments}
        toggleAttachment={toggleAttachment}
        sendingEmail={sendingEmail}
        handleSendEmail={handleSendEmail}
      />

      {/* === Edit Dialogs === */}
      <EditDocumentDialog open={!!editDocDialog} onClose={() => setEditDocDialog(null)} form={editDocForm} setForm={setEditDocForm} onSave={handleSaveDoc} />
      <EditPreauthDialog open={!!editPreauthDialog} onClose={() => setEditPreauthDialog(null)} form={editPreauthForm} setForm={setEditPreauthForm} onSave={handleSavePreauth} />
      <EditDenialDialog open={!!editDenialDialog} onClose={() => setEditDenialDialog(null)} form={editDenialForm} setForm={setEditDenialForm} onSave={handleSaveDenial} />
      <EditFollowupDialog open={!!editFollowupDialog} onClose={() => setEditFollowupDialog(null)} form={editFollowupForm} setForm={setEditFollowupForm} onSave={handleSaveFollowup} />
      <EditPaymentDialog open={!!editPaymentDialog} onClose={() => setEditPaymentDialog(null)} form={editPaymentForm} setForm={setEditPaymentForm} onSave={handleSavePayment} />
    </div>
  );
};

export default ClaimDetailPage;
